import { CreateAppointmentParams, GetAppointmentsParams, UpdateAppointmentParams } from './appointment.types';
import { computeAppointmentStatus, createAppointmentBody, formatAppointment } from './appointmentUtils';
import {
  APPOINTMENT_STATUS_AWAITING_PAYMENT,
  APPOINTMENT_STATUS_AWAITING_CAPTURE,
  PARTICIPANT_STATUS_ACCEPTED,
  PARTICIPANT_STATUS_DECLINED,
  EXPERT,
} from 'FRONTEND/utils/constants';
import { apiStatusError, fetchError } from 'FRONTEND/utils/errors';
import { hl7DateParams } from 'FRONTEND/utils/hl7';
import {
  API_APPOINTMENT_PATH,
  getApiAppointmentPath,
} from 'FRONTEND/utils/routes';

export async function getAppointments({
  actorId,
  actorType,
  startDate,
  endDate,
  withTime,
  isUnrestricted,
}: GetAppointmentsParams) {
  const queryParams = hl7DateParams({ startDate, endDate, withTime });
  if (Array.isArray(actorId)) {
    actorId.forEach((id) => queryParams.append('actor', `Practitioner/${id}`));
  } else if (actorId) {
    queryParams.append(
      'actor',
      `${actorType === EXPERT ? 'Practitioner' : 'MOBL'}/${actorId}`,
    );
  }
  if (isUnrestricted) {
    queryParams.append('unrestricted', 'true');
  }
  try {
    const response = await fetch(
      `${API_APPOINTMENT_PATH}?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      },
    ).then((data) => {
      apiStatusError({ response: data });
      return data.json();
    });

    return response.entry.map((item) => formatAppointment(item));
  } catch (error) {
    throw fetchError({ error });
  }
}

export async function createAppointment(params: CreateAppointmentParams) {
  try {
    const entry = await fetch(API_APPOINTMENT_PATH, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        Prefer: 'return=representation',
        'X-CSRF-Token': window.CSRFToken,
      },
      body: JSON.stringify(createAppointmentBody(params)),
    }).then((data) => {
      apiStatusError({ response: data });
      return data.json();
    });
    return formatAppointment(entry);
  } catch (error) {
    throw fetchError({ error });
  }
}


export async function updateAppointment({
  originalAppointment,
  acceptedIdList = [],
  declinedIdList = [],
  isCancelled = false,
  isForceBooked = false,
  consultationCodeId = null,
}: UpdateAppointmentParams) {
  try {
    const participantList = originalAppointment.participant.map(
      (participant) => {
        const updatedParticipant = { ...participant };

        // Convert resourceType to type for backend compatibility
        // The old and now deprecated endpoint used to do this automatically, but now we need to convert it here.
        // We should maybe refactor it in the entire workflow to "type" instead of "resourceType"
        if (
          updatedParticipant.actor.resourceType &&
          !updatedParticipant.actor.type
        ) {
          updatedParticipant.actor = {
            ...updatedParticipant.actor,
            type: updatedParticipant.actor.resourceType,
          };
          delete updatedParticipant.actor.resourceType;
        }

        if (acceptedIdList.includes(participant.actor.id)) {
          return {
            ...updatedParticipant,
            status: PARTICIPANT_STATUS_ACCEPTED,
          };
        }
        if (declinedIdList.includes(participant.actor.id)) {
          return {
            ...updatedParticipant,
            status: PARTICIPANT_STATUS_DECLINED,
          };
        }
        return updatedParticipant;
      },
    );

    // Update appointment consultation code if provided
    const extensionList = consultationCodeId ? [
      ...originalAppointment.extension.filter(
        (ext) => ext.id !== 'ConsultationCode',
      ),
      { id: 'ConsultationCode', valueInteger: consultationCodeId },
    ] : originalAppointment.extension;

    const body = {
      ...originalAppointment,
      status: computeAppointmentStatus({
        participantList,
        isForceBooked,
        isCancelled,
        isAppointmentWaitingPayment:
          originalAppointment.status === APPOINTMENT_STATUS_AWAITING_PAYMENT,
        isAppointmentWaitingCapture:
          originalAppointment.status === APPOINTMENT_STATUS_AWAITING_CAPTURE,
      }),
      participant: participantList,
      extension: extensionList,
    };
    delete body.requester;
    delete body.requiredMap;
    delete body.patient;
    delete body.appointmentType;
    delete body.roomId;
    delete body.appointmentId;
    delete body.startDate;
    delete body.endDate;
    const entry = await fetch(getApiAppointmentPath(originalAppointment.id), {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        Prefer: 'return=representation',
        'X-CSRF-Token': window.CSRFToken,
      },
      body: JSON.stringify(body),
    }).then((data) => {
      apiStatusError({ response: data });
      return data.json();
    });
    return formatAppointment(entry);
  } catch (error) {
    throw fetchError({ error });
  }
}

export async function deleteAppointment({
  appointmentId,
}: {
  appointmentId: string;
}) {
  try {
    const entry = await fetch(getApiAppointmentPath(appointmentId), {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        Prefer: 'return=representation',
        'X-CSRF-Token': window.CSRFToken,
      },
    }).then((data) => {
      apiStatusError({ response: data });
      return data.json();
    });
    return formatAppointment(entry);
  } catch (error) {
    throw fetchError({ error });
  }
}

